/**
 * Login Streak Badge Component
 * Displays user's login streak and earned badges
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Flame, Star, Trophy, Calendar } from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";

import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";

const LoginStreakBadge = () => {
  const { user } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    
    const fetchStreak = async () => {
      try {
        const res = await axios.get(`${API}/auth/streak`, {
          withCredentials: true
        });
        setStreak(res.data);
      } catch (err) {
        console.error("Failed to load streak:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStreak();
  }, [user]);

  if (loading || !streak) return null;

  const getBadgeIcon = () => {
    if (streak.current_streak >= 30) return <Trophy className="w-5 h-5" />;
    if (streak.current_streak >= 7) return <Star className="w-5 h-5" />;
    if (streak.current_streak >= 3) return <Flame className="w-5 h-5" />;
    return <Calendar className="w-5 h-5" />;
  };

  const getBadgeColor = () => {
    if (streak.current_streak >= 30) return "bg-gradient-to-r from-yellow-400 to-amber-500 text-white";
    if (streak.current_streak >= 7) return "bg-gradient-to-r from-purple-400 to-purple-600 text-white";
    if (streak.current_streak >= 3) return "bg-gradient-to-r from-orange-400 to-red-500 text-white";
    return "bg-stone-100 text-stone-700";
  };

  const getStreakMessage = () => {
    if (streak.current_streak >= 30) return "You're a CatPals Champion!";
    if (streak.current_streak >= 7) return "Amazing dedication!";
    if (streak.current_streak >= 3) return "You're on fire!";
    if (streak.current_streak >= 1) return "Keep it up!";
    return "Start your streak today!";
  };

  return (
    <Card className={`${getBadgeColor()} overflow-hidden`} data-testid="login-streak-badge">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            {getBadgeIcon()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl font-bold">{streak.current_streak}</span>
              <span className="text-sm opacity-90">day streak</span>
              {streak.badge && (
                <Badge className="bg-white/20 text-inherit ml-2">
                  {streak.badge_emoji} {streak.badge}
                </Badge>
              )}
            </div>
            <p className="text-sm opacity-80">{getStreakMessage()}</p>
          </div>
          {streak.best_streak > streak.current_streak && (
            <div className="text-right opacity-80">
              <p className="text-xs">Personal Best</p>
              <p className="font-bold">{streak.best_streak} days</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LoginStreakBadge;
